"use client";
import Link from "next/link";
import Image from "next/image";
import { useReducer } from "react";

import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  Button,
} from "@nextui-org/react";
import { IoChevronDown } from "react-icons/io5";

import { usePathname } from "next/navigation";
import { LoginSection } from "./LoginSection";
import SearchBoxNav from "./SearchBoxNav";

const options = [
  {
    label: "Inicio",
    route: "/",
    subOptions: [],
  },
  {
    label: "Acerca de",
    route: "/about",
    subOptions: [],
  },
  {
    label: "Explorar",
    route: "/explore",
    subOptions: [
      {
        label: "Autores",
        route: "/explore/authors",
      },
      {
        label: "Documentos",
        route: "/explore/documents",
      },
      {
        label: "Organizaciones",
        route: "/explore/organizations",
      },
      {
        label: "Eventos",
        route: "/explore/events",
      },
      {
        label: "Proyectos",
        route: "/explore/projects",
      },
      {
        label: "Patentes",
        route: "/explore/patents",
      },
      {
        label: "Servicios",
        route: "/explore/services",
      },
    ],
  },
  {
    label: "Análisis",
    route: "/indicators",
    subOptions: [
      { label: "Áreas de conocimiento" },
      {
        label: "Redes de investigación",
        route: "/indicators/investigator/networks",
      },
      { label: "Tendencias", route: "/indicators/investigator/trends" },
      {
        label: "Todas las áreas de conocimiento",
        route: "/indicators/investigator/areas",
      },
      { label: "Áreas temáticas de Dewey" },
      { label: "Redes de investigación", route: "/indicators/biblio/networks" },
      { label: "Tendencias", route: "/indicators/biblio/trends" },
      { label: "Todas las áreas temáticas", route: "/indicators/biblio/areas" },
      {
        label: "Objetivos de Desarrollo Sostenible",
      },
      {
        label: "ODS por documentos",
        route: "/indicators/ods/documents",
      },
    ],
  },
  {
    label: "Reportes",
    route: "/reports",
    subOptions: [
      { label: "General", route: "/reports/general" },
      { label: "Autores", route: "/reports/authors" },
      { label: "Documentos", route: "/reports/documents" },
      { label: "Organizaciones", route: "/reports/organizations" },
      { label: "Eventos", route: "/reports/events" },
      { label: "Proyectos", route: "/reports/projects" },
      { label: "Patentes", route: "/reports/patents" },
      { label: "Servicios", route: "/reports/services" },
    ],
  },
];
// Función para agregar IDs de forma dinámica
const addUniqueIds = (options) => {
  return options.map((option, optionIndex) => ({
    ...option,
    id: `option-${optionIndex + 1}`, // Generar ID para la opción
    subOptions: option.subOptions.map((subOption, subIndex) => ({
      ...subOption,
      id: `option-${optionIndex + 1}-subOption-${subIndex + 1}`, // Generar ID para cada subopción
    })),
  }));
};
// Llamamos a la función para generar los IDs dinámicamente
const optionsWithIds = addUniqueIds(options);
export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useReducer((current) => !current, false);
  const pathname = usePathname();

  return (
    <Navbar
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
      isBordered
      maxWidth="full"
      classNames={{
        item: [
          "data-[active=true]:font-bold",
          "data-[active=true]:text-blue-800",
        ],
      }}
    >
      <NavbarMenuToggle
        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        className="md:hidden"
      />
      <NavbarContent className="gap-10" justify="center">
        <NavbarBrand>
          <Link href="/">
            <Image
              src="/logo-vcompleta-redi.svg"
              alt="redi logo"
              width={150}
              height={150}
            />
          </Link>
        </NavbarBrand>
      </NavbarContent>
      {pathname !== "/" && <SearchBoxNav />}

      <NavbarContent className="hidden md:flex gap-10" justify="center">
        {optionsWithIds.map((option) =>
          option.subOptions.length > 0 ? (
            <Dropdown key={option.route}>
              <NavbarItem isActive={option.route === pathname}>
                <DropdownTrigger>
                  <Button
                    disableRipple
                    className={`text-gray-700 p-0 bg-transparent data-[hover=true]:bg-transparent  hover:text-blue-700 ${
                      option.route === pathname && "font-bold text-blue-800"
                    }`}
                    endContent={<IoChevronDown />}
                  >
                    {option.label}
                  </Button>
                </DropdownTrigger>
              </NavbarItem>
              <DropdownMenu>
                <DropdownSection showDivider>
                  <DropdownItem as={Link} href={option.route}>
                    <span className="font-bold">{option.label}</span>
                  </DropdownItem>
                </DropdownSection>
                <DropdownSection>
                  {option.subOptions.map((subOption) =>
                    subOption.route ? (
                      <DropdownItem
                        key={subOption.route}
                        as={Link}
                        href={subOption.route}
                      >
                        {subOption.label}
                      </DropdownItem>
                    ) : (
                      <DropdownItem
                        key={subOption.label}
                        className="pointer-events-none"
                      >
                        <span className="font-bold text-gray">
                          {subOption.label}
                        </span>
                        <hr className="my-1" />
                      </DropdownItem>
                    )
                  )}
                </DropdownSection>
              </DropdownMenu>
            </Dropdown>
          ) : (
            <NavbarItem
              key={option.route}
              isActive={option.route === pathname}
              className="hover:text-blue-700 text-sm"
            >
              <Link href={option.route}>{option.label}</Link>
            </NavbarItem>
          )
        )}
        <NavbarItem>
          <LoginSection />
        </NavbarItem>
      </NavbarContent>
      <NavbarContent className="md:hidden" justify="center">
        <NavbarItem>
          <LoginSection />
        </NavbarItem>
      </NavbarContent>
      <NavbarMenu>
        {optionsWithIds.map(({ label, route }, index) => (
          <NavbarMenuItem key={`${route}-${index}`}>
            <Link
              className={`hover:text-blue-700 w-full ${
                route === pathname && "font-bold text-blue-800"
              }`}
              href={route}
              size="lg"
              onClick={() => setIsMenuOpen()}
            >
              {label}
            </Link>
          </NavbarMenuItem>
        ))}
      </NavbarMenu>
    </Navbar>
  );
}
